import type { NodeV2, NodeV2Style } from '@vedalib/editor'
import type { EditorElement } from '@vedalib/editor/lib/elements'
import { motion } from 'framer-motion'
import React from 'react'
import styled, { css } from 'styled-components'

import { applyRootHeight, applySize } from '../formControls/Size/utils'
import { isAbsolute, isFlex, isGrid } from '../guards'
import AbsoluteItemStyled from './AbsoluteItemStyled'
import AbsoluteStyled from './AbsoluteStyled'
import { CommonStyled } from './CommonStyled'
import FlexItemStyled from './FlexItemStyled'
import FlexStyled from './FlexStyled'
import GridItemStyled from './GridItemStyled'
import GridStyled from './GridStyled'

export interface INodeV2StyledProps {
  $node: NodeV2
  $style: NodeV2Style | null
  $parent: NodeV2 | null
  $parentStyle: NodeV2Style | null
  $element: EditorElement | null
  $isRoot: boolean
  $isSlot: boolean
}

const Root = ({ $style }: INodeV2StyledProps) => css`
  align-self: ${$style?.alignSelf};
  min-width: ${applySize($style?.minWidth)};
  width: ${applySize($style?.width)};
  max-width: ${applySize($style?.maxWidth)};
  min-height: ${applyRootHeight($style?.minHeight)};
  height: ${applyRootHeight($style?.height)};
  max-height: ${applyRootHeight($style?.maxHeight)};
`

const Slot = (_props: INodeV2StyledProps) => css`
  width: 100%;
  height: 100%;
`

const Hidden = ({ $style }: INodeV2StyledProps) =>
  $style?.hidden &&
  css`
    display: none;
  `

const NodeV2Styled = styled(motion.div)<INodeV2StyledProps>`
  position: relative;
  line-height: 0;
  perspective: 1000px;

  ${({ $isRoot }) => $isRoot && Root};
  ${({ $isSlot }) => $isSlot && Slot};

  ${({ $style }) => $style?.hidden && Hidden};

  ${({ $style }) => isGrid($style) && GridStyled};
  ${({ $style }) => isFlex($style) && FlexStyled};
  ${({ $style }) => isAbsolute($style) && AbsoluteStyled};

  ${({ $parentStyle }) => isGrid($parentStyle) && GridItemStyled};
  ${({ $parentStyle }) => isFlex($parentStyle) && FlexItemStyled};
  ${({ $parentStyle }) => isAbsolute($parentStyle) && AbsoluteItemStyled};

  ${CommonStyled};
  ${Hidden};
`

export default React.memo(NodeV2Styled)
